/* Compact Chat Assistant kit — the building blocks of the approved
   "Page redesign: compact layout" (option 1b). Row cards with label + helper on
   the left and the control on the right, 28×16 toggles, segmented controls,
   and the "Advanced" disclosure. Purple and neutral tones only. */

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../../FeatherIcon';
import { Spinner } from '../../Spinner';
import type { AutosaveStatus } from '../../../hooks/useAutosave';
import { C } from './tokens';
import { font as f } from './style';
import './compact.css';


export function Icon({ name, size = 14, color, style }: { name: string; size?: number; color?: string; style?: CSSProperties }) {
  return <FeatherIcon name={name} size={size} style={{ color, ...style }} />;
}

/* ── Toggle (28×16) ─────────────────────────────────────────────── */
export function Toggle({ on, onChange, title, disabled }: { on: boolean; onChange: (v: boolean) => void; title?: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      title={title}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onChange(!on); }}
      style={{ width: 28, height: 16, flex: 'none', borderRadius: 99, border: 0, padding: 2, background: on ? C.purple : C.toggleOff, cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', transition: 'background .15s', opacity: disabled ? 0.5 : 1 }}
    >
      <span style={{ width: 12, height: 12, borderRadius: 99, background: '#fff', transform: on ? 'translateX(12px)' : 'translateX(0)', transition: 'transform .15s' }} />
    </button>
  );
}

/* ── Segmented control ──────────────────────────────────────────── */
export interface SegOpt<T extends string | number> { id: T; label: ReactNode; n?: ReactNode; title?: string; disabled?: boolean }
const SEG_SIZE = {
  sm: { height: 22, padding: '0 8px', font: 11.5 },
  md: { height: 24, padding: '0 10px', font: 12 },
  lg: { height: 26, padding: '0 12px', font: 12.5 },
} as const;
export function Seg<T extends string | number>({ options, value, onChange, size = 'md', disabled, borderColor }: { options: SegOpt<T>[]; value: T | null; onChange: (v: T) => void; size?: 'sm' | 'md' | 'lg'; disabled?: boolean; borderColor?: string }) {
  const z = SEG_SIZE[size];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, padding: 2, flex: 'none', maxWidth: '100%', boxSizing: 'border-box', background: C.input, border: `1px solid ${borderColor ?? C.border}`, borderRadius: 7, opacity: disabled ? 0.5 : 1 }}>
      {options.map((o) => {
        const a = o.id === value;
        const off = disabled || o.disabled;
        return (
          <button
            key={String(o.id)}
            type="button"
            title={o.title}
            disabled={off}
            onClick={() => onChange(o.id)}
            style={{ height: z.height, padding: z.padding, border: 0, borderRadius: 5, background: a ? C.segActive : 'transparent', color: a ? C.text : C.sub, font: f(500, z.font), cursor: off ? 'not-allowed' : 'pointer', opacity: o.disabled && !a ? 0.45 : 1, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}
          >
            {o.label}
            {o.n !== undefined && <span style={{ color: C.muted }}>{o.n}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ── Cards, rows, sections ──────────────────────────────────────── */
export function Card({ children, clip = true, style }: { children: ReactNode; clip?: boolean; style?: CSSProperties }) {
  return (
    <div className="cpt-card" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflow: clip ? 'hidden' : undefined, ...style }}>
      {children}
    </div>
  );
}

/** A settings row: label + one-line helper on the left, control on the right. */
export function Row({ label, help, helpColor, children, stack, extra }: { label: ReactNode; help?: ReactNode; helpColor?: string; children?: ReactNode; stack?: boolean; extra?: ReactNode }) {
  if (stack) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ font: f(500, 13), color: C.text }}>{label}</span>
          {help && <span style={{ font: f(400, 11.5), color: helpColor ?? C.muted }}>{help}</span>}
          {extra}
        </div>
        {children}
      </div>
    );
  }
  return (
    <div className="cpt-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 14px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ font: f(500, 13), color: C.text }}>{label}</span>
        {help && <span style={{ font: f(400, 11.5), color: helpColor ?? C.sub }}>{help}</span>}
      </div>
      <div className="cpt-row-ctl" style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none', font: f(400, 12), color: C.muted, whiteSpace: 'nowrap' }}>{children}</div>
    </div>
  );
}

export function Section({ title, hint, right, children, gap = 8 }: { title?: ReactNode; hint?: ReactNode; right?: ReactNode; children: ReactNode; gap?: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      {(title || right) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
            {title && <span style={{ font: f(600, 13), color: C.text }}>{title}</span>}
            {hint && <span style={{ font: f(400, 11.5), color: C.muted }}>{hint}</span>}
          </div>
          {right && <><span style={{ flex: 1 }} />{right}</>}
        </div>
      )}
      {children}
    </div>
  );
}

export function Stack({ children, gap = 14 }: { children: ReactNode; gap?: number }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap }}>{children}</div>;
}

/* ── Inputs ─────────────────────────────────────────────────────── */
const inputStyle = (width?: number | string): CSSProperties => ({
  width: width ?? '100%', height: 28, boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 6, padding: '0 8px', color: C.text, font: f(500, 12.5), outline: 'none', flex: width ? 'none' : undefined, minWidth: 0,
});

export function TextInput({ value, onChange, placeholder, width, title, disabled, mono }: { value: string; onChange: (v: string) => void; placeholder?: string; width?: number | string; title?: string; disabled?: boolean; mono?: boolean }) {
  return <input value={value} title={title} disabled={disabled} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={{ ...inputStyle(width), fontWeight: mono ? 400 : 500 }} />;
}

/**
 * Numeric input that keeps its own text while typing ("", "1.", "0.5") and
 * reports a parsed number (or null when empty and `allowEmpty`). Out-of-range or
 * unparsable text isn't reported, so a half-typed value never gets saved.
 */
export function NumInput({ value, onChange, width = 56, placeholder, min, max, int, allowEmpty, disabled, title }: {
  value: number | null | undefined; onChange: (v: number | null) => void; width?: number | string; placeholder?: string; min?: number; max?: number; int?: boolean; allowEmpty?: boolean; disabled?: boolean; title?: string;
}) {
  const fmt = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));
  const [text, setText] = useState(fmt(value));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(fmt(value));
  }, [value]);
  const parse = (s: string): number | null | undefined => {
    const t = s.trim().replace(',', '.');
    if (t === '') return allowEmpty ? null : undefined;
    const n = Number(t);
    if (!Number.isFinite(n)) return undefined;
    if (int && !Number.isInteger(n)) return undefined;
    if (min !== undefined && n < min) return undefined;
    if (max !== undefined && n > max) return undefined;
    return n;
  };
  const bad = parse(text) === undefined;
  return (
    <input
      value={text}
      inputMode={int ? 'numeric' : 'decimal'}
      placeholder={placeholder}
      disabled={disabled}
      title={title}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; setText(fmt(value)); }}
      onChange={(e) => {
        setText(e.target.value);
        const n = parse(e.target.value);
        if (n !== undefined && n !== value) onChange(n);
      }}
      style={{ ...inputStyle(width), borderColor: bad ? C.purplePale : C.border }}
    />
  );
}

export function TextArea({ value, onChange, placeholder, height = 60, disabled }: { value: string; onChange: (v: string) => void; placeholder?: string; height?: number; disabled?: boolean }) {
  return (
    <textarea
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ height, minHeight: 44, boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 9px', color: C.text, font: f(400, 12.5, 1.45), outline: 'none', resize: 'vertical', width: '100%' }}
    />
  );
}

export function Select<T extends string>({ value, options, onChange, width = 220, disabled }: { value: T; options: { id: T; label: string }[]; onChange: (v: T) => void; width?: number; disabled?: boolean }) {
  return (
    <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value as T)} style={{ width, flex: 'none', height: 28, background: C.input, border: `1px solid ${C.border}`, borderRadius: 6, padding: '0 8px', color: C.text, font: f(500, 12), outline: 'none' }}>
      {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );
}

/** Custom dropdown with a one-line note per option and a check on the selected one. */
export function Dropdown<T extends string>({ value, options, onChange, width = 150, menuWidth = 260 }: { value: T; options: { id: T; label: string; sub?: string }[]; onChange: (v: T) => void; width?: number; menuWidth?: number }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState(0);
  const cur = options.find((o) => o.id === value) ?? options[0];
  // The menu hangs off the button's right edge; on a phone that can push it past the left edge of the screen.
  useLayoutEffect(() => {
    if (!open || !menuRef.current) { setShift(0); return; }
    const r = menuRef.current.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const base = r.left - shift;
    setShift(base < 8 ? 8 - base : base + r.width > vw - 8 ? Math.max(vw - 8 - r.width - base, 8 - base) : 0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- measured once per open
  useEffect(() => {
    if (!open) return;
    // Capture phase: Esc closes this menu only, not the modal it may sit in.
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    window.addEventListener('keydown', k, true);
    return () => window.removeEventListener('keydown', k, true);
  }, [open]);
  return (
    <div style={{ position: 'relative', flex: 'none' }}>
      <button type="button" onClick={() => setOpen((o) => !o)} style={{ width, height: 28, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px 0 10px', background: C.input, border: `1px solid ${open ? C.purple : C.border}`, borderRadius: 6, color: C.text, font: f(500, 12.5), cursor: 'pointer', textAlign: 'left' }}>
        <span title={cur?.label} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cur?.label}</span>
        <Icon name="chevron-down" size={14} color={C.sub} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 9 }} />
          {/* whiteSpace: parents like <Row> set nowrap, which would push option notes out of the menu. */}
          <div ref={menuRef} style={{ position: 'absolute', top: 32, right: 0, zIndex: 10, width: menuWidth, maxWidth: 'calc(100vw - 16px)', transform: shift ? `translateX(${shift}px)` : undefined, padding: 4, boxSizing: 'border-box', whiteSpace: 'normal', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 8, boxShadow: '0 12px 32px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {options.map((o) => {
              const a = o.id === value;
              return (
                <button key={o.id} type="button" className="cpt-hover-row" onClick={() => { onChange(o.id); setOpen(false); }} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 8px', border: 0, borderRadius: 6, background: a ? 'color-mix(in srgb, var(--om-accent) 12%, transparent)' : 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                    <span style={{ font: f(500, 12.5), color: a ? C.text : 'var(--om-body)' }}>{o.label}</span>
                    {o.sub && <span style={{ font: f(400, 11.5), color: C.muted }}>{o.sub}</span>}
                  </span>
                  <Icon name="check" size={14} color={C.purpleLt} style={{ opacity: a ? 1 : 0, marginTop: 1 }} />
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Buttons ────────────────────────────────────────────────────── */
type BtnKind = 'ghost' | 'primary' | 'tint' | 'dashed' | 'text' | 'muted';
export function Btn({ kind = 'ghost', onClick, children, icon, disabled, busy, title, height = 28, style }: { kind?: BtnKind; onClick?: () => void; children?: ReactNode; icon?: string; disabled?: boolean; busy?: boolean; title?: string; height?: number; style?: CSSProperties }) {
  const k: Record<BtnKind, CSSProperties> = {
    ghost: { background: 'transparent', border: `1px solid ${C.borderHi}`, color: C.text },
    muted: { background: 'transparent', border: `1px solid ${C.borderHi}`, color: C.sub },
    primary: { background: C.purple, border: 0, color: '#fff', fontWeight: 600 },
    tint: { background: C.tint16, border: `1px solid ${C.tintLine45}`, color: C.text },
    dashed: { background: 'transparent', border: `1px dashed ${C.borderHi}`, color: C.purplePale },
    text: { background: 'transparent', border: 0, color: C.purplePale },
  };
  return (
    <button type="button" title={title} disabled={disabled} onClick={onClick} className={[kind === 'primary' ? '' : 'cpt-hover', busy ? 'om-busy' : ''].filter(Boolean).join(' ') || undefined} style={{ height, flex: 'none', padding: '0 11px', borderRadius: 6, font: f(500, 12), cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5, opacity: disabled ? 0.5 : 1, ...k[kind], ...style }}>
      {icon && <Icon name={icon} size={13} />}
      {children}
    </button>
  );
}

export function XButton({ onClick, title }: { onClick: () => void; title?: string }) {
  return <button type="button" title={title} onClick={onClick} className="cpt-hover-text" style={{ width: 26, height: 26, flex: 'none', background: 'transparent', border: 0, color: C.muted, font: f(400, 16), cursor: 'pointer' }}>×</button>;
}

/** Icon-only re-sync button for a vault / list picker; spins while `onRefresh` runs. */
export function RefreshBtn({ onRefresh, title, busy, size = 26 }: { onRefresh: () => Promise<unknown>; title: string; busy?: boolean; size?: number }) {
  const [own, setOwn] = useState(false);
  const spinning = own || !!busy;
  const go = async () => {
    setOwn(true);
    try { await onRefresh(); } catch { /* the picker keeps its last list */ } finally { setOwn(false); }
  };
  return (
    <button type="button" onClick={() => void go()} disabled={spinning} title={title} className={`cpt-hover-row${spinning ? ' om-busy' : ''}`} style={{ width: size, height: size, flex: 'none', border: 0, borderRadius: 6, background: 'transparent', color: C.sub, cursor: spinning ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon name="refresh-cw" size={13} />
    </button>
  );
}

/** "› Advanced  what's inside" disclosure — closed by default. */
export function AdvancedToggle({ open, onToggle, hint, label }: { open: boolean; onToggle: () => void; hint?: string; label?: string }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <button type="button" onClick={onToggle} className="cpt-hover" style={{ alignSelf: 'flex-start', height: 28, display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px 0 6px', border: 0, borderRadius: 6, background: 'transparent', color: C.purpleLt, font: f(500, 12.5), cursor: 'pointer', whiteSpace: 'nowrap' }}>
      <Icon name="chevron-right" size={14} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
      {label ?? t('advanced')}
      {hint && <span style={{ font: f(400, 11.5), color: C.muted, whiteSpace: 'normal', textAlign: 'left' }}>{hint}</span>}
    </button>
  );
}

/* ── Notes ──────────────────────────────────────────────────────── */
export function InfoLine({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, font: f(400, 11.5), color: C.sub }}>
      <span style={{ width: 14, height: 14, flex: 'none', borderRadius: 99, border: `1px solid ${C.muted}`, font: f(600, 9), display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.sub }}>{'i'}</span>
      <span>{children}</span>
    </div>
  );
}

export function Warn({ children }: { children: ReactNode }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: f(500, 11.5), color: C.purplePale }}>
      <Icon name="alert-circle" size={14} />
      {children}
    </span>
  );
}

export function Empty({ children, pad = 12 }: { children: ReactNode; pad?: number }) {
  return <div style={{ padding: `${pad}px 14px`, font: f(400, 12), color: C.muted }}>{children}</div>;
}

export function Pill({ children, title }: { children: ReactNode; title?: string }) {
  return <span title={title} style={{ font: f(500, 11.5), color: C.purplePale, padding: '4px 9px', border: `1px solid ${C.tintLine}`, borderRadius: 99, whiteSpace: 'nowrap' }}>{children}</span>;
}

export function Badge({ children }: { children: ReactNode }) {
  return <span style={{ font: f(500, 10.5), color: C.purplePale, padding: '1px 7px', border: `1px solid ${C.tintLine}`, borderRadius: 99, whiteSpace: 'nowrap' }}>{children}</span>;
}

/* ── Tables (10px uppercase header, 40px rows) ──────────────────── */
export function THead({ cols, children }: { cols: string; children: ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 10, padding: '7px 14px', font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>{children}</div>;
}
export function TRow({ cols, children, height }: { cols: string; children: ReactNode; height?: number }) {
  return <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 10, alignItems: 'center', minHeight: height ?? 40, padding: '6px 14px', boxSizing: 'border-box' }}>{children}</div>;
}
export function CellLabel({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0 }}>
      <span style={{ font: f(500, 13), color: C.text }}>{children}</span>
      {sub && <span style={{ font: f(400, 11.5), color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</span>}
    </div>
  );
}
export function Foot({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', font: f(400, 11.5), color: C.muted }}>{children}</div>;
}

/* ── Animation bits (analysis) ──────────────────────────────────── */
export function Spin({ size = 12 }: { size?: number }) {
  return <Spinner size={size} />;
}

/** Purple tint with a pulse and a moving sweep, laid over a tile being analysed. */
export function Scan() {
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', background: 'color-mix(in srgb, var(--om-accent) 10%, transparent)', boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--om-accent-2) 45%, transparent)', borderRadius: 8, animation: 'cpt-pulse 1.6s ease-in-out infinite', zIndex: 1 }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '45%', background: 'linear-gradient(180deg,transparent,color-mix(in srgb, var(--om-accent-2) 40%, transparent),transparent)', animation: 'cpt-scan 1.6s ease-in-out infinite' }} />
    </div>
  );
}

export function RunTag({ children }: { children: ReactNode }) {
  return <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 99, background: C.tint16, font: f(500, 11), color: C.purplePale, whiteSpace: 'nowrap' }}><Spin />{children}</span>;
}

/* ── Save status (autosave) ─────────────────────────────────────── */
export function SaveStatus({ status, error, onRetry, idleLabel }: { status: AutosaveStatus; error?: string | null; onRetry?: () => void; idleLabel?: boolean }) {
  const { t } = useTranslation('assistantCompact');
  const base: CSSProperties = { display: 'flex', alignItems: 'center', gap: 5, font: f(400, 11.5), whiteSpace: 'nowrap' };
  if (status === 'saving') return <span style={{ ...base, color: C.muted }}><Spinner size={12} label={t('save.saving')} /></span>;
  if (status === 'saved') return <span style={{ ...base, color: C.purplePale }}><Icon name="check" size={12} />{t('save.saved')}</span>;
  if (status === 'error' || status === 'invalid') {
    return (
      <span style={{ ...base, color: C.purplePale }} title={error ?? undefined}>
        <Icon name="alert-circle" size={13} />
        {status === 'invalid' ? (error ?? t('save.invalid')) : t('save.failed')}
        {status === 'error' && onRetry && (
          <button type="button" onClick={onRetry} style={{ border: 0, background: 'transparent', color: C.purpleLt, font: f(600, 11.5), cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>{t('save.retry')}</button>
        )}
      </span>
    );
  }
  if (!idleLabel) return null;
  return <span style={{ ...base, color: C.muted }}><Icon name="check" size={12} />{t('save.auto')}</span>;
}

/* ── Page frame: section rail + header + optional sub-tabs ──────── */
/** `meta` shows a small count badge in place of the toggle (e.g. "4/4" fields filled).
 *  `icon` is a small muted marker before it (e.g. a lock on a read-only section). */
export interface RailRow { id: string; label: string; on?: boolean | null; onToggle?: (v: boolean) => void; toggleTitle?: string; meta?: string; metaTitle?: string; icon?: string; iconTitle?: string }

export function SectionRail({ heading, items, active, onSelect, width = 196 }: { heading: string; items: RailRow[]; active: string; onSelect: (id: string) => void; width?: number }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <aside className="cpt-rail" style={{ width, flex: 'none', borderRight: `1px solid ${C.divider}`, padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: 1, boxSizing: 'border-box' }}>
      <span style={{ font: f(600, 10), letterSpacing: '.08em', textTransform: 'uppercase', color: C.muted, padding: '0 10px 6px', width: '100%' }}>{heading}</span>
      {items.map((s) => {
        const a = s.id === active;
        return (
          <div
            key={s.id}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(s.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(s.id); } }}
            className={a ? undefined : 'cpt-hover-row'}
            style={{ height: 30, padding: '0 8px 0 10px', borderRadius: 6, background: a ? C.tint16 : 'transparent', color: a ? C.text : C.sub, font: f(500, 12.5), cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, textAlign: 'left' }}
          >
            <span title={s.label} style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
            {s.onToggle && s.on !== null && s.on !== undefined && (
              <Toggle on={s.on} onChange={s.onToggle} title={s.toggleTitle ?? (s.on ? t('rail.onTip') : t('rail.offTip'))} />
            )}
            {s.icon && <span title={s.iconTitle} style={{ display: 'flex', flex: 'none' }}><Icon name={s.icon} size={11} color={C.muted} /></span>}
            {s.meta && (
              <span title={s.metaTitle} style={{ flex: 'none', font: f(600, 10.5), fontVariantNumeric: 'tabular-nums', color: a ? C.purplePale : C.sub, background: a ? C.tint20 : 'var(--om-card-2)', padding: '1px 6px', borderRadius: 99, whiteSpace: 'nowrap' }}>{s.meta}</span>
            )}
          </div>
        );
      })}
    </aside>
  );
}

export function PageFrame({ rail, headline, desc, right, tabs, children }: { rail?: ReactNode; headline: ReactNode; desc?: ReactNode; right?: ReactNode; tabs?: ReactNode; children: ReactNode }) {
  return (
    <div className="cpt-root cpt-layout" style={{ display: 'flex', minHeight: 480, background: C.surface, border: `1px solid ${C.borderLo}`, borderRadius: 10, overflow: 'visible' }}>
      {rail}
      <main className="cpt-main" style={{ flex: 1, minWidth: 0, padding: '16px 24px 40px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="cpt-head" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="cpt-head-title" style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
            <span style={{ font: f(700, 17, 1.2), color: C.text }}>{headline}</span>
            {desc && <span style={{ font: f(400, 12), color: C.sub }}>{desc}</span>}
          </div>
          {right}
        </div>
        {tabs}
        {children}
      </main>
    </div>
  );
}

export function SubTabs<T extends string>({ options, value, onChange }: { options: { id: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div style={{ alignSelf: 'flex-start' }}>
      <Seg options={options} value={value} onChange={onChange} size="lg" />
    </div>
  );
}

export function Loading() {
  const { t } = useTranslation('assistantCompact');
  return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 4px' }}><Spinner size={20} label={t('loading')} /></div>;
}

export function ErrorLine({ children }: { children: ReactNode }) {
  return <div style={{ padding: '8px 12px', border: `1px solid ${C.tintLine}`, borderRadius: 8, font: f(400, 12), color: C.purplePale, display: 'flex', gap: 6, alignItems: 'center' }}><Icon name="alert-circle" size={14} />{children}</div>;
}

